import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@cntrl/tokens/css";
import "@cntrl/tokens/fonts";
import "./styles/global.scss";
import { lockNativeViewport, markBundleHealthy, trackKeyboardInset } from "@cntrl/native";
import { Bootstrap } from "./app/Bootstrap.js";
import { completeOAuthPopupIfReturning } from "./app/oauthPopup.js";
import { renderOAuthPopupNotice } from "./app/oauthPopupNotice.js";
import { applyStoredThemeAtBoot } from "./app/theme.js";
import { applyTouchPickerPreviewAtBoot } from "./app/touchPickerPreview.js";
import { installClientVersion } from "./app/clientVersion.js";
import { applyDownloadedUpdate, startLiveUpdates } from "./app/liveUpdate.js";

// Native only (no-op on web): this bundle started, so the live updater keeps it. FIRST, before anything that can
// fail or wait — a bundle that does not say so within 10 s is rolled back to the previous one.
void markBundleHealthy();

// Native shell only (no-op on web): pin the viewport so focusing an input never zooms — WebKit
// auto-zooms sub-16px inputs, leaving the app horizontally scrollable (2026-08-12 simulator pass).
lockNativeViewport();
// The soft keyboard's height as --keyboard-inset, which the mobile shell and every fixed overlay subtract (ART-251).
trackKeyboardInset();

// If this document is the OAuth popup coming back from the callback, report the outcome and close. Runs
// BEFORE render so the band never sees the app flash inside a 520px window on its way out.
if (completeOAuthPopupIfReturning()) {
  // Closing — mounting the app here would boot a second session in a window that is about to disappear.
  // `window.close()` can be refused though (a COOP-severed popup on some engines), so leave words on the
  // screen rather than the branded loader spinning over a window with nothing left to do.
  renderOAuthPopupNotice();
} else {

  // Seed the app-wide theme from the stored (dev-only) preference before first paint, so a dark preference
  // does not flash light on load. DOS's mobile force is applied later by ShellLayout once routing resolves.
  applyStoredThemeAtBoot();
  // Non-production: the desktop preview of the touch dropdown sheet (cookie or `?touchPickers=1`, ART-266).
  applyTouchPickerPreviewAtBoot();
  // Every API request says which client is asking, and a 426 turns into a reload or the update screen. BEFORE the
  // app mounts: openapi-fetch and SignalR capture `fetch` when they are built.
  installClientVersion();
  // Native only: a newer SPA bundle downloaded on an earlier run is switched to HERE, at the start of a cold launch,
  // before anything renders — the app reloads into it behind the boot loader. Otherwise boot, and look for a newer
  // bundle now and on every return to the foreground (liveUpdate.ts). On web this resolves false at once.
  void applyDownloadedUpdate().then((switching) => {
    if (switching) return;
    startLiveUpdates();

    const rootEl = document.getElementById("root");
    if (!rootEl) throw new Error("Root element #root not found.");

    createRoot(rootEl).render(
      <StrictMode>
        <Bootstrap />
      </StrictMode>,
    );
  });
}
